import { get, derived, writable } from 'svelte/store';
import _ from 'lodash';
import { __ } from '@oat-sa-private/ui-core';
import configuration from '../stores/configuration.js';
import { isEmpty } from '../helpers/objectHelper.js';

const defaultLocale = 'en-US';
// TODO: remove locales hardcode
const supportedLocales = [defaultLocale, 'es-ES', 'fr-FR', 'gl-ES', 'hu-HU', 'ja-JP', 'lt-LT', 'pt-BR', 'sk-SK'];

/**
 * @param {string} targetLocale
 * @returns {string}
 */
function mapToSupportedLocale(targetLocale) {
    return supportedLocales.find(supportedLocale =>
        supportedLocale === targetLocale ||
        supportedLocale.split('-')[0] === targetLocale // if browserLocale === 'en', 'lt', etc
    );
}

/**
 * @returns {string}
 */
export function defineLocale() {
    const browserLocale = navigator.language;
    let currentLocale = mapToSupportedLocale(browserLocale);

    if (!currentLocale) {
        let otherSupportedLocale = '';

        navigator.languages.find(language => {
            otherSupportedLocale = mapToSupportedLocale(language) || otherSupportedLocale;
            return language;
        });

        return otherSupportedLocale || defaultLocale;
    }

    return currentLocale;
}

function cleanKey(key) {
    if (!key || typeof key !== 'string') {
        return '';
    }
    let myKey = key.split('.').pop();
    if (!myKey) {
        return '';
    }
    myKey = myKey.replace(/_/g, ' ');
    myKey = myKey.charAt(0).toUpperCase() + myKey.slice(1);
    return myKey;
}

/**
 * @param {string} _locale
 * @param {string} key
 * @param {Object} [vars]
 * @param {string} [fallback]
 * @returns {string}
 */
function translate(_locale, key, vars, fallback) {
    const config = get(configuration);
    const translations = getTranslations(config, _locale);
    if (!key) throw new Error('no key provided to $t()');

    let text = translations[key] || fallback || cleanKey(key);

    if (!text) throw new Error(`no translation found for ${_locale}.${key}`);

    // Replace any passed in variables in the translation string
    Object.keys(vars).map((k) => {
        const regex = new RegExp(`{{${k}}}`, 'g');
        text = text.replace(regex, vars[k]);
    });

    return text;
}

/**
 * Dynamically import a dictionary from a locale
 * @param {string} _locale - the locale to load
 * @returns {Promise<Object>} resolves with the dictionary
 */
async function loadDictionary(_locale) {
    await configuration.loadingPromise;
    const config = get(configuration);
    return getTranslations(config, _locale);
}

/**
 * @param {{}} config
 * @param {string} _locale
 * @returns {{[string]: string}}
 */
function getTranslations(config, _locale) {
    if (!config[_locale] || _.isEmpty(config[_locale])) {
        _locale = defaultLocale;
    }
    const translations = typeof config[_locale] === 'string' ? JSON.parse(config[_locale]) : config[_locale];
    if (!translations || isEmpty(translations)) {
        throw new Error('translations are not available');
    }
    if (!_locale) {
        throw new Error('locale not provided');
    }

    return translations;
}

export const locale = defineLocale();

// TODO: move fully to translator from @oat-sa-private/ui-core
// as we can't translate LDS components with our custom translator
// BUT! Add translation prefixes support to the tool first

//register the dictionary loader
__.setDictionaryLoader(_locale =>
    loadDictionary(_locale)
        .catch(() => loadDictionary(defaultLocale))
        .then(dictionaryModule => dictionaryModule)
);

__.setLocale(locale);

export const t = derived(writable(locale), ($locale) => (key, vars = {}, fallback = '') =>
    translate($locale, key, vars, fallback)
);
